import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  BalanceAdjustmentRow,
  BalanceAdjustmentType,
  updateBalanceAdjustment,
} from "@/Api/balanceAdjustments";

interface CorrectBalanceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  type: BalanceAdjustmentType;
  typeLabel: string;
  record: BalanceAdjustmentRow | null;
  recordLabel: string;
  onSaved: () => void;
}

/**
 * Modal de correção manual de balanço.
 *
 * Permite alterar apenas balanço e valor preso, exigindo confirmação
 * explícita antes de enviar a alteração.
 */
export const CorrectBalanceModal = ({
  open,
  onOpenChange,
  type,
  typeLabel,
  record,
  recordLabel,
  onSaved,
}: CorrectBalanceModalProps) => {
  const [balance, setBalance] = useState<string>("");
  const [withHeldBalance, setWithHeldBalance] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open && record) {
      setBalance(String(record.balance ?? 0));
      setWithHeldBalance(String(record.with_held_balance ?? 0));
      setReason("");
      setIsConfirming(false);
      setIsSaving(false);
    }
  }, [open, record]);

  if (!record) return null;

  const parsedBalance = Number(balance);
  const parsedWithHeld = Number(withHeldBalance);

  const isValid =
    balance !== "" &&
    withHeldBalance !== "" &&
    Number.isInteger(parsedBalance) &&
    Number.isInteger(parsedWithHeld) &&
    parsedBalance >= 0;

  const hasChanges =
    parsedBalance !== Number(record.balance) ||
    parsedWithHeld !== Number(record.with_held_balance);

  const handleContinue = () => {
    if (!isValid) {
      toast.error("Informe números inteiros. O balanço não pode ser negativo.");
      return;
    }

    if (!hasChanges) {
      toast.error("Nenhum valor foi alterado.");
      return;
    }

    setIsConfirming(true);
  };

  const handleConfirm = async () => {
    setIsSaving(true);

    const response = await updateBalanceAdjustment(type, record.uuid, {
      balance: parsedBalance,
      with_held_balance: parsedWithHeld,
      reason: reason.trim() ? reason.trim() : undefined,
      confirmed: true,
    });

    setIsSaving(false);

    if (!response.ok) {
      setIsConfirming(false);
      return;
    }

    toast.success("Balanço corrigido com sucesso.");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (isSaving ? null : onOpenChange(value))}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Corrigir balanço</DialogTitle>
          <DialogDescription>
            {typeLabel} — {recordLabel}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          <div className="flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              Correção manual e direta dos campos de balanço. Nenhum recálculo é
              executado e a alteração é registrada em auditoria.
            </span>
          </div>
        </div>

        {isConfirming ? (
          <div className="space-y-4">
            <p className="text-sm font-medium">Confirma a alteração abaixo?</p>

            <div className="rounded-md border divide-y text-sm">
              <div className="flex items-center justify-between p-3">
                <span className="text-muted-foreground">Balanço</span>
                <span>
                  <span className="line-through text-muted-foreground">
                    {record.balance}
                  </span>{" "}
                  → <span className="font-semibold">{parsedBalance}</span>
                </span>
              </div>
              <div className="flex items-center justify-between p-3">
                <span className="text-muted-foreground">Valor preso</span>
                <span>
                  <span className="line-through text-muted-foreground">
                    {record.with_held_balance}
                  </span>{" "}
                  → <span className="font-semibold">{parsedWithHeld}</span>
                </span>
              </div>
              {reason.trim() && (
                <div className="flex items-start justify-between gap-4 p-3">
                  <span className="text-muted-foreground">Motivo</span>
                  <span className="text-right">{reason.trim()}</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setIsConfirming(false)}
                disabled={isSaving}
              >
                Voltar
              </Button>
              <Button onClick={handleConfirm} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Confirmar alteração
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Balanço atual</Label>
              <Input value={record.balance} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-balance">Novo balanço</Label>
              <Input
                id="new-balance"
                type="number"
                min={0}
                step={1}
                value={balance}
                onChange={(event) => setBalance(event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>Valor preso atual</Label>
              <Input value={record.with_held_balance} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-with-held-balance">Novo valor preso</Label>
              <Input
                id="new-with-held-balance"
                type="number"
                step={1}
                value={withHeldBalance}
                onChange={(event) => setWithHeldBalance(event.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Esta coluna aceita valores negativos no banco.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Motivo (opcional)</Label>
              <Textarea
                id="reason"
                maxLength={500}
                rows={3}
                value={reason}
                placeholder="Ex.: correção de inconsistência gerada por distribuição duplicada"
                onChange={(event) => setReason(event.target.value)}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button onClick={handleContinue}>Salvar</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
